/**
 * Spook Supply — product page helpers (loaded on product templates only).
 *
 *  <ss-shipping-note>   Free UK delivery message + progress. Uses the basket total plus the
 *                       selected variant × quantity. Threshold comes from Theme settings
 *                       (rendered into data-threshold) — checkout shipping rates are not
 *                       readable from the storefront.
 *  <ss-recommendations> Lazy loader for Shopify product recommendations (complementary →
 *                       related fallback). Hides its section when nothing comes back.
 *  [data-ss-open-details] "Read more" link that opens and scrolls to the Product details row.
 *
 * Relies only on Horizon/Shopify standard events; never touches the add-to-cart flow.
 */
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const cartUrl = () => `${window.Shopify?.routes?.root || '/'}cart.js`;

/* ------------------------------------------------------------------ */
/* Basket total, shared by every note on the page                      */
/* ------------------------------------------------------------------ */
const basket = {
  total: null,
  discounted: false,
  listeners: new Set(),
  pending: null,
  refresh() {
    this.pending ??= fetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (cart && typeof cart.total_price === 'number') {
          this.total = cart.total_price;
          // With a discount applied, an added item isn't worth its full price towards the
          // threshold, so the note falls back to the simple, always-true message.
          this.discounted = Number(cart.total_discount) > 0;
          this.listeners.forEach((fn) => fn(this.total));
        }
      })
      .catch(() => {})
      .finally(() => {
        this.pending = null;
      });
    return this.pending;
  },
};

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const done = event?.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve();
  done.catch(() => {}).finally(() => basket.refresh());
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) basket.refresh();
});

/* ------------------------------------------------------------------ */
/* <ss-shipping-note>                                                  */
/* ------------------------------------------------------------------ */
class SsShippingNote extends HTMLElement {
  #abort = new AbortController();
  #quantity = 1;

  connectedCallback() {
    const { signal } = this.#abort;
    const section = this.closest('.shopify-section') || document;

    this.threshold = Number(this.dataset.threshold) || 0;
    this.price = Number(this.dataset.price) || 0;
    this.available = this.dataset.available !== 'false';
    this.basketTotal = Number(this.dataset.basket) || 0;
    this.#quantity = this.#readQuantity(section);

    section.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity, { signal });
    section.addEventListener(
      'change',
      (e) => {
        if (e.target instanceof HTMLInputElement && e.target.name === 'quantity') this.#onQuantity(e);
      },
      { signal }
    );

    this.discounted = this.dataset.discounted === 'true';
    this.onBasket = (total) => {
      this.basketTotal = total;
      this.discounted = basket.discounted;
      this.render();
    };
    basket.listeners.add(this.onBasket);
    // The page may be served from cache: confirm the live basket once.
    basket.refresh();
  }

  disconnectedCallback() {
    this.#abort.abort();
    basket.listeners.delete(this.onBasket);
  }

  #readQuantity(root) {
    const input = root.querySelector('.product-details input[name="quantity"], product-form-component input[name="quantity"]');
    const q = parseInt(input?.value || '1', 10);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  #onQuantity = (event) => {
    const fromDetail = Number(event?.detail?.quantity);
    const fromInput = event?.target instanceof HTMLInputElement ? parseInt(event.target.value, 10) : NaN;
    const q = Number.isFinite(fromDetail) && fromDetail > 0 ? fromDetail : fromInput;
    if (Number.isFinite(q) && q > 0 && q !== this.#quantity) {
      this.#quantity = q;
      this.render();
    }
  };

  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        const next = html.querySelector(`ss-shipping-note[data-block-id="${this.dataset.blockId}"]`);
        if (!next) return;
        this.price = Number(next.dataset.price) || 0;
        this.available = next.dataset.available !== 'false';
        this.render();
      })
      .catch(() => {});
  };

  money(cents) {
    const format = this.dataset.moneyFormat || '£{{amount}}';
    const currency = this.dataset.currency || 'GBP';
    try {
      return formatMoney(cents, format, currency);
    } catch {
      return `£${(cents / 100).toFixed(2)}`;
    }
  }

  moneyShort(cents) {
    const s = this.money(cents);
    return cents % 100 === 0 ? s.replace(/[.,]00(?=\D*$)/, '') : s;
  }

  render() {
    const t = this.threshold;
    if (!t) return;
    const projected = this.basketTotal + this.price * this.#quantity;
    let state = 'away';
    if (!this.available) state = 'info';
    else if (this.basketTotal >= t) state = 'basket';
    else if (this.discounted) state = 'info';
    else if (projected >= t) state = this.basketTotal === 0 ? 'item' : 'add';

    const free = '<strong>FREE UK delivery</strong>';
    const multiple = this.#quantity > 1;
    const text = {
      basket: `Your basket qualifies for ${free}`,
      item: multiple ? `Add these and your order qualifies for ${free}` : `This item qualifies for ${free}`,
      add: `Add ${multiple ? 'these' : 'this'} and your order qualifies for ${free}`,
      info: `${free} on orders of ${this.moneyShort(t)} or more`,
      away: `Add ${multiple ? 'these' : 'this'} and you'll be <strong>${this.money(t - projected)}</strong> away from FREE UK delivery`,
    }[state];

    const fill = state === 'basket' ? 100 : Math.max(0, Math.min(100, Math.round((projected / t) * 100)));
    const textEl = this.querySelector('[data-ss-ship-text]');
    if (textEl && textEl.innerHTML !== text) textEl.innerHTML = text;
    this.querySelector('[data-ss-ship-bar]')?.style.setProperty('--ss-ship-fill', `${fill}%`);
    this.dataset.state = state;
  }
}

if (!customElements.get('ss-shipping-note')) customElements.define('ss-shipping-note', SsShippingNote);

/* ------------------------------------------------------------------ */
/* <ss-recommendations>                                                */
/* ------------------------------------------------------------------ */
class SsRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.state) return;
    this.dataset.state = 'idle';
    if (!('IntersectionObserver' in window)) return void this.load();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.observer.disconnect();
          this.load();
        }
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async fetchIntent(intent) {
    const res = await fetch(`${this.dataset.url}&intent=${intent}`);
    if (!res.ok) return null;
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const result = doc.querySelector('[data-ss-recs-result]');
    const list = result?.querySelector('.ss-recs__list');
    return list && list.children.length ? list : null;
  }

  async load() {
    this.dataset.state = 'loading';
    try {
      let list = await this.fetchIntent(this.dataset.intent || 'related');
      if (!list && this.dataset.fallback) list = await this.fetchIntent(this.dataset.fallback);
      const slot = this.querySelector('[data-ss-recs-slot]');
      if (list && slot) {
        slot.replaceChildren(document.importNode(list, true));
        this.dataset.state = 'loaded';
        return;
      }
    } catch {
      /* fall through to empty */
    }
    this.dataset.state = 'empty';
    const note = this.querySelector('[data-ss-recs-empty]');
    if (note) {
      note.hidden = false;
      this.querySelector('[data-ss-recs-slot]')?.replaceChildren();
    } else {
      this.closest('.ss-recs')?.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('ss-recommendations')) customElements.define('ss-recommendations', SsRecommendations);

/* ------------------------------------------------------------------ */
/* Rating row → this product's reviews (#product-reviews)              */
/* ------------------------------------------------------------------ */
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function goToReviews({ smooth = true, focus = true } = {}) {
  const dest = document.getElementById('product-reviews');
  if (!dest) return false;
  // 'instant' (not 'auto'): the theme sets scroll-behavior: smooth on <html>, which 'auto' would inherit.
  dest.scrollIntoView({ behavior: smooth && !reduceMotion() ? 'smooth' : 'instant', block: 'start' });
  if (focus) dest.querySelector('h2')?.focus({ preventScroll: true });
  return true;
}

// Arriving from a product card link (…/products/handle#product-reviews): images above the
// section load after the browser's first jump, so settle on the section once the page has loaded.
if (location.hash === '#product-reviews') {
  window.addEventListener('load', () => goToReviews({ smooth: false, focus: false }), { once: true });
}

/* ------------------------------------------------------------------ */
/* "Read more" → open Product details                                  */
/* ------------------------------------------------------------------ */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const rating = target?.closest('a[href="#product-reviews"]');
  if (rating) {
    if (goToReviews()) {
      event.preventDefault();
      history.replaceState(null, '', '#product-reviews');
    }
    return;
  }
  const link = target?.closest('[data-ss-open-details]');
  if (!link) return;
  const row = document.getElementById('ss-product-details');
  if (!(row instanceof HTMLDetailsElement)) return;
  event.preventDefault();
  row.open = true;
  row.scrollIntoView({ behavior: reduceMotion() ? 'instant' : 'smooth', block: 'start' });
  row.querySelector('summary')?.focus({ preventScroll: true });
});

/* ------------------------------------------------------------------ */
/* <ss-delivery-timeline>: estimated calendar dates                    */
/* ------------------------------------------------------------------ */
/* Business days are Monday–Friday. The server renders the plain ranges
   ("1–3 business days"); this only swaps in estimated dates for the
   customer's own "today". Bank holidays are not known here, which is why
   the dates are labelled as estimates. */
class SsDeliveryTimeline extends HTMLElement {
  connectedCallback() {
    if (this.dataset.showDates !== 'true' || this.dataset.enhanced === 'true') return;
    const n = (key, fallback) => {
      const v = parseInt(this.dataset[key], 10);
      return Number.isFinite(v) && v >= 0 ? v : fallback;
    };
    let dMin = n('dispatchMin', 1);
    let dMax = n('dispatchMax', 3);
    let vMin = n('deliveryMin', 5);
    let vMax = n('deliveryMax', 8);
    if (dMax < dMin) [dMin, dMax] = [dMax, dMin];
    if (vMax < vMin) [vMin, vMax] = [vMax, vMin];

    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const shipped = this.querySelector('[data-ss-delivery-value="shipped"]');
    const delivered = this.querySelector('[data-ss-delivery-value="delivered"]');
    if (!shipped || !delivered) return;

    shipped.textContent = this.range(this.addBusinessDays(today, dMin), this.addBusinessDays(today, dMax));
    delivered.textContent = this.range(this.addBusinessDays(today, vMin), this.addBusinessDays(today, vMax));
    this.querySelectorAll('[data-ss-delivery-note]').forEach((el) => (el.hidden = false));
    this.dataset.enhanced = 'true';
  }

  addBusinessDays(from, days) {
    const d = new Date(from);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    // "0 days" from a weekend still means the next business day
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
    return d;
  }

  range(a, b) {
    const day = (d) => d.getDate();
    const mon = (d) => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
    if (a.getTime() === b.getTime()) return `${day(a)} ${mon(a)}`;
    if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) return `${day(a)}–${day(b)} ${mon(b)}`;
    return `${day(a)} ${mon(a)} – ${day(b)} ${mon(b)}`;
  }
}

if (!customElements.get('ss-delivery-timeline')) customElements.define('ss-delivery-timeline', SsDeliveryTimeline);
